'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getOrders, loadMoreOrders, cancelOrder } from '../../redux/actions/orderActions';
import { formatDateFR } from '../../utils/dateFormatter';

const LIMIT = 15;

const SortIcon = ({ column, sortBy, sortOrder }) => {
  if (sortBy !== column) return <i className="material-icons sort-icon">unfold_more</i>;
  return <i className="material-icons sort-icon active">{sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>;
};

const OrdersList = () => {
  const dispatch = useDispatch();
  const { orders, loading, loadingMore, hasMore, error } = useSelector(state => state.orders);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const buildParams = useCallback((page) => ({
    page,
    limit: LIMIT,
    search: searchTerm || undefined,
    status: statusFilter || undefined,
    dateFrom: dateRange.from || undefined,
    dateTo: dateRange.to || undefined,
    sortBy,
    sortOrder
  }), [searchTerm, statusFilter, dateRange, sortBy, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
    dispatch(getOrders(buildParams(1)));
  }, [dispatch, buildParams]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          dispatch(loadMoreOrders(buildParams(nextPage)));
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [dispatch, hasMore, loadingMore, loading, currentPage, buildParams]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateRange({ from: '', to: '' });
  };

  const handleCancelOrder = async (id) => {
    try {
      await dispatch(cancelOrder(id));
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'accepted': return 'Accepté';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      case 'cancelled': return 'Annulé';
      case 'sent': return 'Expédié';
      default: return status;
    }
  };

  return (
    <div className="orders-list-container">
      <div className="page-header">
        <h1>Mes commandes</h1>
      </div>

      <div className="filters-card">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input">
            <i className="material-icons">search</i>
            <input
              type="text"
              placeholder="Rechercher par nom de soumission..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
        </form>

        <div className="filters-grid">
          <div className="filter-group">
            <label>Statut</label>
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="form-control"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="accepted">Accepté</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Terminé</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Date début</label>
            <input
              type="date"
              name="from"
              value={dateRange.from}
              onChange={handleDateChange}
              className="form-control"
            />
          </div>

          <div className="filter-group">
            <label>Date fin</label>
            <input
              type="date"
              name="to"
              value={dateRange.to}
              onChange={handleDateChange}
              className="form-control"
            />
          </div>

          <div className="filter-actions">
            <button className="btn btn-outline-secondary" onClick={handleResetFilters}>
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement des commandes...</p>
        </div>
      ) : orders?.items?.length > 0 ? (
          <div className="dashboard-card">
            <div className="table-responsive" ref={scrollContainerRef}>
              <table className="table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('id')}>
                      ID <SortIcon column="id" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th>Soumission</th>
                    <th className="sortable" onClick={() => handleSort('created_at')}>
                      Date de création <SortIcon column="created_at" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('updated_at')}>
                      Dernière mise à jour <SortIcon column="updated_at" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('status')}>
                      Statut <SortIcon column="status" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.items.map(order => (
                    <tr key={order.id}>
                      <td>#{order.id}</td>
                      <td>
                        <Link to={`/submissions/${order.submission.id}`}>
                          {order.submission.name}
                        </Link>
                      </td>
                      <td>{formatDateFR(order.createdAt)}</td>
                      <td>{formatDateFR(order.updatedAt)}</td>
                      <td>
                        <span className={`status-badge ${order.status}`}>
                          {formatStatus(order.status)}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <Link to={`/orders/${order.id}`} className="btn-icon">
                            <i className="material-icons">visibility</i>
                          </Link>
                          {(order.status === 'pending' || order.status === 'accepted' || order.status === 'in_progress') && (
                            <button
                              className="btn-icon danger"
                              title="Annuler la commande"
                              onClick={() => handleCancelOrder(order.id)}
                            >
                              <i className="material-icons">cancel</i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} style={{ height: 1 }} />
              {loadingMore && (
                <div className="loading-more">
                  <div className="spinner spinner-sm"></div>
                  <span>Chargement...</span>
                </div>
              )}
              {!hasMore && orders.items.length > 0 && (
                <p className="list-end-message">Toutes les commandes sont affichées</p>
              )}
            </div>
          </div>
      ) : (
        <div className="empty-state">
          <i className="material-icons">shopping_cart</i>
          <h2>Aucune commande</h2>
          <p>Vous n'avez pas encore de commandes.</p>
          <Link to="/submissions" className="btn btn-primary">
            Voir mes soumissions
          </Link>
        </div>
      )}
    </div>
  );
};

export default OrdersList;
